import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Copy, CheckCircle2, Mail, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export const OnboardingInviteDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', preferred_language: 'de' });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [notifyTrainers, setNotifyTrainers] = useState(true);
  const genericLink = `${window.location.origin}/onboarding/start`;

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/onboarding/notify-settings`, authHeader())
      .then(r => setNotifyTrainers(r.data?.notify_trainers !== false))
      .catch(() => {});
  }, [open]);

  const toggleNotify = async (val) => {
    setNotifyTrainers(val);
    try { await axios.put(`${API}/onboarding/notify-settings`, { notify_trainers: val }, authHeader()); }
    catch { toast.error('Einstellung konnte nicht gespeichert werden'); }
  };

  const copyGeneric = () => { navigator.clipboard?.writeText(genericLink); toast.success('Sammel-Link kopiert'); };

  const reset = () => { setForm({ first_name: '', last_name: '', email: '', phone: '', preferred_language: 'de' }); setResult(null); };

  const submit = async () => {
    if (!form.email.trim() && !form.phone.trim()) { toast.error('Bitte E-Mail oder Handynummer angeben'); return; }
    setSaving(true);
    try {
      const body = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        preferred_language: form.preferred_language,
      };
      if (form.email.trim()) body.email = form.email.trim();
      if (form.phone.trim()) body.phone = form.phone.trim();
      const res = await axios.post(`${API}/onboarding/invite`, body, authHeader());
      setResult(res.data);
      const sent = res.data.sent || {};
      if (sent.email || sent.whatsapp) toast.success('Kunde angelegt & Link gesendet');
      else toast.success('Kunde angelegt — Link kann kopiert/geteilt werden');
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const copyLink = () => {
    if (result?.onboarding_link) {
      navigator.clipboard?.writeText(result.onboarding_link);
      toast.success('Link kopiert');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="onboarding-invite-open-btn">
          <UserPlus className="h-4 w-4 mr-2" /> Kunde + Onboarding-Link
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="onboarding-invite-dialog">
        <DialogHeader>
          <DialogTitle>Neuer Kunde — Daten selbst ausfüllen lassen</DialogTitle>
          <DialogDescription>
            Lege den Kunden mit den Basisdaten an. Er erhält einen Link (E-Mail/WhatsApp) und
            trägt Stammdaten, Gesundheitsfragebogen &amp; Einverständnis selbst ein.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            {/* Generischer Sammel-Link — kein Name/keine Daten nötig */}
            <div className="rounded-lg border p-3 bg-muted/40">
              <Label className="text-xs text-gray-500">Generischer Sammel-Link (für alle, ohne Daten)</Label>
              <div className="flex gap-2 mt-1">
                <Input readOnly value={genericLink} className="text-xs" data-testid="onboarding-generic-link" />
                <Button variant="outline" size="icon" onClick={copyGeneric} data-testid="onboarding-generic-copy"><Copy className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="text-xs text-center text-muted-foreground">— oder persönlich anlegen —</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vorname</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="optional" data-testid="onboarding-invite-firstname" /></div>
              <div><Label>Nachname</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="optional" data-testid="onboarding-invite-lastname" /></div>
            </div>
            <div><Label>E-Mail</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="optional, wenn Handy vorhanden" data-testid="onboarding-invite-email" /></div>
            <div><Label>Handynummer</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+41 79 …" data-testid="onboarding-invite-phone" /></div>
            <p className="text-xs text-muted-foreground">Name ist optional — der Kunde trägt alles selbst ein. Mindestens E-Mail oder Handynummer angeben, damit der Link versendet werden kann.</p>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="onboarding-notify-trainers-toggle">
              <input type="checkbox" checked={notifyTrainers} onChange={e => toggleNotify(e.target.checked)} />
              Nach Abschluss auch alle Trainer benachrichtigen (sonst nur Admin)
            </label>
            <DialogFooter>
              <Button onClick={submit} disabled={saving} data-testid="onboarding-invite-submit">
                {saving ? 'Legt an…' : 'Anlegen & Link senden'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4" data-testid="onboarding-invite-result">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-5 h-5" /> <span className="font-medium">Kunde angelegt</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className={`flex items-center gap-1 ${result.sent?.email ? 'text-green-600' : 'text-gray-400'}`}>
                <Mail className="w-4 h-4" /> {result.sent?.email ? 'E-Mail gesendet' : (result.email ? 'E-Mail nicht gesendet' : 'keine E-Mail')}
              </span>
              <span className={`flex items-center gap-1 ${result.sent?.whatsapp ? 'text-green-600' : 'text-gray-400'}`}>
                <MessageCircle className="w-4 h-4" /> {result.sent?.whatsapp ? 'WhatsApp gesendet' : (result.phone ? 'WhatsApp nicht gesendet' : 'kein WhatsApp')}
              </span>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Onboarding-Link (zum Kopieren/Teilen)</Label>
              <div className="flex gap-2 mt-1">
                <Input readOnly value={result.onboarding_link} className="text-xs" data-testid="onboarding-invite-link" />
                <Button variant="outline" size="icon" onClick={copyLink} data-testid="onboarding-invite-copy"><Copy className="w-4 h-4" /></Button>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={reset} data-testid="onboarding-invite-another">Weiteren anlegen</Button>
              <Button onClick={() => { setOpen(false); reset(); }} data-testid="onboarding-invite-done">Fertig</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingInviteDialog;
